import { useEffect, useRef } from 'react';
import { Mail, Phone, Github, Linkedin, MapPin, Youtube, Instagram,  } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { profileData } from '@/data/profile';
import { SiLeetcode } from 'react-icons/si';
import BlurText from './BlurText';

export default function Contact() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let ctx: any = null;
    const run = async () => {
      if (typeof window === 'undefined') return;
      const gsapMod = await import('gsap');
      const gsap = (gsapMod.gsap || gsapMod.default || gsapMod) as any;
      const st = await import('gsap/ScrollTrigger');
      const ScrollTrigger = st.ScrollTrigger || st.default || st;
      if (gsap && ScrollTrigger && !gsap._contactScrollRegistered) {
        gsap.registerPlugin(ScrollTrigger);
        gsap._contactScrollRegistered = true;
      }

      ctx = gsap.context(() => {
        // Animate each contact card separately
        const cards = gsap.utils.toArray('.contact-card') as HTMLElement[];
        cards.forEach((card, i) => {
          gsap.fromTo(
            card,
            { y: 40, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.6,
              delay: i * 0.15,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: card,
                start: 'top 85%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        });

        // Animate social links individually
        const links = gsap.utils.toArray('.social-link') as HTMLElement[];
        links.forEach((link, i) => {
          gsap.fromTo(
            link,
            { scale: 0, opacity: 0 },
            {
              scale: 1,
              opacity: 1,
              duration: 0.5,
              delay: i * 0.1,
              ease: 'back.out(1.7)',
              scrollTrigger: {
                trigger: link,
                start: 'top 90%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        });
      }, sectionRef);
    };

    run();

    return () => {
      if (ctx?.revert) ctx.revert();
    };
  }, []);


  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: profileData.contacts.email,
      href: `mailto:${profileData.contacts.email}`,
      color: 'text-primary'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: profileData.contacts.phone,
      href: `tel:${profileData.contacts.phone}`,
      color: 'text-secondary'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Mumbra, Maharashtra',
      href: '#',
      color: 'text-primary'
    }
  ];

  const socialLinks = [
    {
      icon: Linkedin,
      label: 'LinkedIn',
      href: profileData.contacts.linkedin,
      color: 'hover:text-blue-400'
    },
    {
      icon: Github,
      label: 'GitHub',
      href: profileData.contacts.github,
      color: 'hover:text-foreground'
    },
    {
      icon: Youtube,
      label: 'Youtube',
      href: profileData.contacts.youtube,
      color: 'hover:text-primary'
    },
    {
      icon: SiLeetcode,
      label: 'Leetcode',
      href: profileData.contacts.leetcode,
      color: 'hover:text-primary'
    },
    {
      icon: Instagram,
      label: 'Instagram',
      href: profileData.contacts.instagram,
      color: 'hover:text-primary'
    },
  ];

  const handleAnimationComplete = () => {

  }

  return (
    <section id="contact" ref={sectionRef} className="py-20 px-4" aria-label="Contact section">
      <div className="max-w-4xl mx-auto">
        <div className="text-center md:mb-16 mb-8 flex flex-col items-center">
          {/* <h2 className="text-center bg-gradient-to-r from-cyan-600 to-purple-400 bg-clip-text font-extrabold text-transparent tracking-tighter lg:text-7xl md:text-5xl text-5xl w-fit">Get in Touch</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Let's collaborate and build something amazing together
          </p> */}
          <BlurText
              text="Get in Touch"
              delay={100}
              animateBy="words"
              direction="top"
              onAnimationComplete={handleAnimationComplete}
              className="text-center bg-gradient-to-r from-cyan-600 to-purple-400 bg-clip-text text-transparent font-extrabold tracking-tighter lg:text-7xl md:text-5xl text-5xl justify-center"
            />

            <BlurText
            text="Let's collaborate and build something amazing together"
            delay={30}
            animateBy="words"
            direction="top"
            onAnimationComplete={handleAnimationComplete}
            className="md:text-xl text-lg max-w-3xl mx-auto text-center flex justify-center bg-muted-foreground bg-clip-text"
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold text-foreground mb-6">Contact Information</h3>
            
            {contactMethods.map((method, index) => {
              const IconComponent = method.icon;
              
              return (
                <div
                  key={method.label}
                  className="contact-card glass-card md:p-6 p-3 group cursor-pointer"
                  onClick={() => {
                    if (method.href !== '#') {
                      window.open(method.href, '_blank');
                    }
                  }}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-xl bg-glass-bg/50 flex items-center justify-center ${method.color} group-hover:scale-110 transition-transform`}>
                      <IconComponent className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{method.label}</h4>
                      <p className="text-muted-foreground group-hover:text-foreground transition-colors text-sm md:text-md">
                        {method.value}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Social Links */}
            <div className="social-links pt-8">
              <h4 className="font-medium text-foreground mb-4">Connect on Social</h4>
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`social-link w-12 h-12 glass-card flex items-center justify-center text-muted-foreground ${social.color} transition-all duration-300 hover:scale-110`}
                      aria-label={social.label}
                    >
                      <IconComponent className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Contact Form / CTA */}
          <div className="contact-card glass-card md:p-8 p-5">
            <h3 className="text-2xl font-semibold text-foreground mb-6">Let's Work Together</h3>
            
            <div className="space-y-6">
              <p className="text-muted-foreground leading-relaxed">
                I'm always open to discussing new opportunities, innovative projects, 
                and ways to contribute to meaningful technological solutions. Whether you're 
                looking for a developer, need consultation, or just want to connect, I'd love to hear from you.
              </p>

              <div className="space-y-4">
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                  <h4 className="font-medium text-primary mb-2">💼 Open for Opportunities</h4>
                  <p className="text-sm text-muted-foreground">
                    Full-time positions, internships, and freelance projects
                  </p>
                </div>
                
                <div className="p-4 bg-secondary/5 border border-secondary/20 rounded-xl">
                  <h4 className="font-medium text-secondary mb-2">🤝 Collaboration Ready</h4>
                  <p className="text-sm text-muted-foreground">
                    Open source projects, hackathons, and technical discussions
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => window.open(`mailto:${profileData.contacts.email}`, '_blank')}
                  className="w-full bg-primary text-white/80 hover:text-white hover:bg-primary/90 font-medium py-6 text-lg rounded-xl "
                  aria-label="Send Email"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Send Email
                </Button>
                
                <Button
                  onClick={() => window.open(profileData.contacts.linkedin, '_blank')}
                  //@ts-ignore
                  variant="outline"
                  className="w-full border-glass-border/50 bg-glass-bg/30 text-white/70 hover:bg-glass-bg/50 font-medium py-6 text-lg rounded-xl hover:text-blue-500"
                  aria-label="Connect on LinkedIn"
                >
                  <Linkedin className="mr-2 h-5 w-5" />
                  Connect on LinkedIn
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}